import React, { useContext, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Image,
  Icon,
  useWindowDimensions,
  useContainerDimensions,
  useGlobalToggle,
} from 'edcom-ui';

import { listen } from '../../../services/events';
import { openSignup } from '../../../services/regModal';
import { openPayWall } from '../../../services/payWallModal';
import memberService from '../../../services/memberApi';
import AccountContext from '../../../services/AccountContext';
import RequestContext from '../../../services/RequestContext';

import styles from './GameIFrame.module.less';
import { DISPATCH_OPEN_GAMEIFRAME_EVENT } from './constants';
import { openGameIFrame } from './util';

const GameIFrame = ({
  defaultLessonPath,
  internalName,
  imgSrc,
  alt,
  id,
  title,
  subject,
  grade,
  allowAnonPlay,
  inGuidedLesson,
  contentType,
  canDownload,
}) => {
  const { ready, memberId, isPremium, isUnder13 } = useContext(AccountContext);
  const { referrer } = useContext(RequestContext);
  const [lessonPath, setLessonPath] = useState(defaultLessonPath);

  // vars for resizing iframe
  const windowDimensions = useWindowDimensions();
  const containerRef = useRef(null);
  const containerDimensions = useContainerDimensions(containerRef);
  const iFrameRef = useRef(null);
  const [maximized, setMaximized] = useGlobalToggle(iFrameRef, false);

  // vars for allowing gameplay
  const [singlePlayToken, setSinglePlayToken] = useState(null);
  const allowPlay =
    isPremium || isUnder13 || singlePlayToken || canDownload || allowAnonPlay;

  useEffect(() => {
    return listen(DISPATCH_OPEN_GAMEIFRAME_EVENT, clickPlay);
  }, [memberId]);

  // hook to fetch credits or singlePlayToken and update lessonPath accordingly
  useEffect(() => {
    function updateLessonPathForSinglePlayToken() {
      switch (subject) {
        case '':
          return setLessonPath(`singlePlay:${id},${subject},${grade}`);
        case 'typing':
          return setLessonPath(`singlePlay:${id},,`);
        default:
          return inGuidedLesson
            ? setLessonPath(`singlePlay:${id},${subject},${subject}-${grade}`)
            : defaultLessonPath;
      }
    }

    if (ready && memberId && !isPremium) {
      memberService.fetchSinglePlayToken(referrer, memberId, id).then(token => {
        setSinglePlayToken(token);
        updateLessonPathForSinglePlayToken();
      });
    }
  }, [ready, memberId]);

  // hook to update window.contentItem for kidframe
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.contentItem = {
        id,
        type: 'game',
        section: 'Game Detail',
        name: title,
        loggedIn: !!memberId,
        proMember: isPremium,
        premium: isPremium,
        openPaywall: !allowPlay,
        singlePlayToken: singlePlayToken,
      };
    }
  }, [memberId, isPremium, allowPlay, singlePlayToken]);

  function calculateContainerStyle(maximized) {
    if (!maximized)
      return {
        width: `${containerDimensions.width}px`,
        height: `${containerDimensions.width * (800 / 1280)}px`,
      };

    let tempWidth;
    let tempHeight;
    if (windowDimensions.width < 500) {
      tempWidth = windowDimensions.width - 20;
      tempHeight = windowDimensions.height - 15;
    } else if (windowDimensions.width < 750) {
      tempWidth = windowDimensions.width - 40;
      tempHeight = windowDimensions.height - 30;
    } else {
      tempWidth = windowDimensions.width - 80;
      tempHeight = windowDimensions.height - 60;
    }

    return {
      width: `${Math.min(tempWidth, tempHeight * (1280 / 800))}px`,
      height: `${Math.min(tempHeight, tempWidth * (800 / 1280))}px`,
    };
  }

  const style = {
    transform: 'translateY(0)',
    WebkitTransform: 'translateY(0)',
    MozTransform: 'translateY(0)',
    msTransform: 'translateY(0)',
  };

  const handleLoadGame = event => {
    const iframe = event.target;
    iframe.contentWindow.document.addEventListener('click', handleClickPlay);
  };

  const clickPlay = () => {
    if (!iFrameRef.current) {
      openWall();
    } else {
      try {
        iFrameRef.current.contentWindow.clickPlay();
        setMaximized(true);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleClickPlay = event => {
    const classList = event.target.classList;
    if (
      classList.contains('play_button') ||
      classList.contains('play') ||
      classList.contains('paused') ||
      classList.contains('paused_button')
    ) {
      setMaximized(true);
    } else if (classList.contains('pause')) {
      minimizeGame();
    }
  };

  const minimizeGame = () => {
    iFrameRef.current.contentWindow.clickClose();
    setMaximized(false);
  };

  function handleOpenSignup() {
    openSignup('game', {
      signUp: {
        title: 'Join Education.com for Free Access to Games',
        description:
          "You'll get access to free games and other resources by becoming a member of Education.com.",
      },
    });
  }

  function handleOpenPayWall() {
    openPayWall({
      contentType: contentType,
      contentId: id,
      feature: 'game',
    });
  }

  const openWall = () => {
    !memberId ? handleOpenSignup() : handleOpenPayWall();
  };

  let game;
  if (!ready) {
    game = null;
  } else if (!memberId || !allowPlay) {
    game = (
      <div className="gamePlaceholder">
        <Image src={imgSrc} alt={alt} className={styles.img} />
        <div className={styles.overlay} />
        <div className={clsx(styles.playButton)} onClick={openWall}>
          <Icon name="play" className={styles.playIcon} />
        </div>
      </div>
    );
  } else {
    const gameSrc = `/games/play/?path=${lessonPath}&gameUrl=${internalName}&pageType=detail`;
    game = (
      <>
        <div className={clsx(styles.overlay, maximized && styles.maximized)} />
        <div
          className={clsx('gameIframeHolder')}
          style={calculateContainerStyle(maximized)}
        >
          <div
            className={clsx(styles.close, maximized && styles.maximized)}
            onClick={minimizeGame}
          >
            <Icon name="cancel-light" />
          </div>
          <iframe
            seamless
            className={clsx(styles.iframe, 'game_holder')}
            data-src={gameSrc}
            src={gameSrc}
            style={style}
            sandbox="allow-same-origin allow-scripts"
            ref={iFrameRef}
            onLoad={handleLoadGame}
          />
        </div>
      </>
    );
  }

  return (
    <div
      className={clsx(styles.container, maximized && styles.maximized)}
      ref={containerRef}
    >
      {game}
    </div>
  );
};

GameIFrame.propTypes = {
  defaultLessonPath: PropTypes.string.isRequired,
  internalName: PropTypes.string.isRequired,
  imgSrc: PropTypes.string,
  alt: PropTypes.string,
  id: PropTypes.string,
  title: PropTypes.string,
  subject: PropTypes.string,
  grade: PropTypes.string,
  allowAnonPlay: PropTypes.bool,
  inGuidedLesson: PropTypes.bool,
  contentType: PropTypes.string,
  canDownload: PropTypes.bool.isRequired,
};

export default GameIFrame;
export { openGameIFrame };
